import { useState, useEffect } from "react"
import { X, MapPin, Loader2, Bell, Repeat, Video } from "lucide-react"
import { createAppointment } from "../../services/appointments"
import { appointmentToCalendarEvent } from "../../utils/appointmentAdapter"
import { Button } from "components/ui/button"
import { Input } from "components/ui/input"
import { Textarea } from "components/ui/textarea"
import { Label } from "components/ui/label"
import { cn } from "lib/utils"
import { ParticipantSelector } from "./ParticipantSelector"
import type { CalendarEvent, EventType } from "../../types/agenda"
import { CREATABLE_EVENT_TYPES } from "../../constants/agenda"

interface NewEventModalProps {
  onClose: () => void
  onSave: (event: CalendarEvent) => void
  initialDate?: Date
}

export function NewEventModal({ onClose, onSave, initialDate }: NewEventModalProps) {
  const [title, setTitle] = useState("")
  const [eventType, setEventType] = useState<EventType>("meeting")
  const [date, setDate] = useState(() => {
    if (initialDate) {
      const y = initialDate.getFullYear()
      const m = String(initialDate.getMonth() + 1).padStart(2, "0")
      const d = String(initialDate.getDate()).padStart(2, "0")
      return `${y}-${m}-${d}`
    }
    return ""
  })
  const [startTime, setStartTime] = useState(() => {
    if (initialDate && (initialDate.getHours() !== 0 || initialDate.getMinutes() !== 0)) {
      const h = String(initialDate.getHours()).padStart(2, "0")
      const m = String(initialDate.getMinutes()).padStart(2, "0")
      return `${h}:${m}`
    }
    return ""
  })
  const [endTime, setEndTime] = useState("")
  const [location, setLocation] = useState("")
  const [selectedTeamMembers, setSelectedTeamMembers] = useState<number[]>([])
  const [selectedContact, setSelectedContact] = useState<number | null>(null)
  const [description, setDescription] = useState("")
  const [isSaving, setIsSaving] = useState(false)
  const [saveError, setSaveError] = useState<string | null>(null)

  // Auto-calcola end_time come start_time + 1 ora quando start_time cambia
  useEffect(() => {
    if (startTime && !endTime) {
      const [hours, minutes] = startTime.split(":").map(Number)
      const endDate = new Date(0, 0, 0, hours + 1, minutes)
      const endHours = String(endDate.getHours()).padStart(2, "0")
      const endMinutes = String(endDate.getMinutes()).padStart(2, "0")
      setEndTime(`${endHours}:${endMinutes}`)
    }
  }, [startTime])

  const handleSave = async () => {
    // Validation
    if (!title.trim() || !date) {
      setSaveError("Titolo e Data sono campi obbligatori")
      return
    }

    if ((eventType === "meeting" || eventType === "attivita") && !startTime) {
      setSaveError("L'ora di inizio è obbligatoria per Meeting e Attività")
      return
    }

    // Mappa tipo frontend → backend
    const typeMapping: Record<string, string> = {
      meeting: 'MEETING',
      evento: 'EVENT',
      attivita: 'APPOINTMENT',  // "attività" mappa a APPOINTMENT nel backend
    }

    // Mappa i campi frontend → backend
    const appointmentData: Record<string, any> = {
      title,
      appointment_type: typeMapping[eventType] || 'MEETING',
      date,                                        // start_date → date
      start_time: startTime || "09:00",
      ...(endTime ? { end_time: endTime } : {}),   // manda end_time solo se compilato
      participants: selectedTeamMembers,
    }
    // Aggiungi campi opzionali solo se hanno valore
    if (location) appointmentData.location = location
    if (description) appointmentData.description = description
    if (selectedContact !== null) appointmentData.client = selectedContact

    setIsSaving(true)
    setSaveError(null)

    try {
      const response = await createAppointment(appointmentData)
      // Mappa la risposta backend → formato CalendarEvent per il frontend
      const savedEvent = appointmentToCalendarEvent(response.data)
      onSave(savedEvent)
      handleClose()
    } catch (error: any) {
      const data = error?.response?.data
      let message: string
      if (data?.detail) {
        message = data.detail
      } else if (data && typeof data === "object") {
        message = Object.entries(data)
          .map(([field, errs]) => `${field}: ${Array.isArray(errs) ? errs.join(", ") : errs}`)
          .join(" • ")
      } else {
        message = error?.message || "Errore durante il salvataggio"
      }
      setSaveError(message)
    } finally {
      setIsSaving(false)
    }
  }

  const handleClose = () => {
    setTitle("")
    setEventType("meeting")
    setDate("")
    setStartTime("")
    setEndTime("")
    setLocation("")
    setSelectedTeamMembers([])
    setSelectedContact(null)
    setDescription("")
    onClose()
  }

  return (
    <div className="flex flex-col h-full bg-[var(--hexa-surface-elevated)]">
      {/* Header */}
      <div className="flex-shrink-0 border-b border-[var(--hexa-border)] px-4 py-3">
        <div className="flex items-center justify-between">
          <h2 className="text-base font-semibold !text-[var(--hexa-text-primary)]">
            {eventType === 'evento' ? 'Nuovo Evento' : eventType === 'attivita' ? 'Nuova Attività' : 'Nuovo Meeting'}
          </h2>
          <Button
            variant="ghost"
            size="icon"
            onClick={handleClose}
            aria-label="Chiudi nuovo evento"
            className="text-[var(--hexa-text-secondary)] hover:text-[var(--hexa-text-primary)] hover:bg-[var(--hexa-border)]"
          >
            <X className="h-4 w-4" />
          </Button>
        </div>
      </div>

      {/* Form Content - Scrollable */}
      <div className="flex-1 overflow-y-auto p-4 space-y-3 scrollbar-hexa">
        {/* Titolo */}
        <div className="space-y-1.5">
          <Label className="text-xs font-medium text-[var(--hexa-text-secondary)]">
            Titolo <span className="text-red-400">*</span>
          </Label>
          <Input
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="Es. Riunione mensile"
            className="bg-[var(--hexa-button-cancel)] border-[var(--hexa-border)] text-[var(--hexa-text-primary)] placeholder:text-[var(--hexa-text-muted)]"
          />
        </div>

        {/* Tipo di Evento */}
        <div className="space-y-1.5">
          <Label className="text-xs font-medium text-[var(--hexa-text-secondary)]">Tipo di Evento</Label>
          <div className="grid grid-cols-3 gap-1.5">
            {CREATABLE_EVENT_TYPES.map((type) => {
              const Icon = type.icon
              const isSelected = eventType === type.id
              return (
                <button
                  key={type.id}
                  type="button"
                  onClick={() => setEventType(type.id)}
                  className={cn(
                    "flex items-center gap-2 px-2.5 py-2 rounded-md transition-all border",
                    isSelected
                      ? "border-transparent text-white"
                      : "bg-[var(--hexa-button-cancel)] border-[var(--hexa-border)] text-[var(--hexa-text-secondary)] hover:text-[var(--hexa-text-primary)] hover:border-[var(--hexa-border)]",
                  )}
                  style={{
                    backgroundColor: isSelected ? type.color : undefined,
                  }}
                >
                  <Icon className="h-3.5 w-3.5" />
                  <span className="text-xs font-medium">{type.label}</span>
                </button>
              )
            })}
          </div>
        </div>

        {/* Data */}
        <div className="space-y-1.5">
          <Label className="text-xs font-medium text-[var(--hexa-text-secondary)]">
            Data <span className="text-red-400">*</span>
          </Label>
          <Input
            type="date"
            value={date}
            onChange={(e) => setDate(e.target.value)}
            className="bg-[var(--hexa-button-cancel)] border-[var(--hexa-border)] text-[var(--hexa-text-primary)] placeholder:text-[var(--hexa-text-muted)]"
          />
        </div>

        {/* Ora Inizio + Ora Fine */}
        <div className="grid grid-cols-2 gap-2.5">
          <div className="space-y-2">
            <Label className="text-xs font-medium text-[var(--hexa-text-secondary)]">
              Ora Inizio
              {(eventType === "meeting" || eventType === "attivita") && (
                <span className="text-red-400 ml-0.5">*</span>
              )}
            </Label>
            <Input
              type="time"
              value={startTime}
              onChange={(e) => setStartTime(e.target.value)}
              className="bg-[var(--hexa-button-cancel)] border-[var(--hexa-border)] text-[var(--hexa-text-primary)] placeholder:text-[var(--hexa-text-muted)]"
            />
          </div>
          <div className="space-y-2">
            <Label className="text-xs font-medium text-[var(--hexa-text-secondary)]">Ora Fine</Label>
            <Input
              type="time"
              value={endTime}
              onChange={(e) => setEndTime(e.target.value)}
              className="bg-[var(--hexa-button-cancel)] border-[var(--hexa-border)] text-[var(--hexa-text-primary)] placeholder:text-[var(--hexa-text-muted)]"
            />
          </div>
        </div>

        {/* Luogo */}
        <div className="space-y-1.5">
          <Label className="text-xs font-medium text-[var(--hexa-text-secondary)]">Luogo</Label>
          <div className="relative">
            <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-[var(--hexa-text-muted)]" />
            <Input
              value={location}
              onChange={(e) => setLocation(e.target.value)}
              placeholder="Es. Sala riunioni A, Google Meet, etc."
              className="bg-[var(--hexa-button-cancel)] border-[var(--hexa-border)] text-[var(--hexa-text-primary)] placeholder:text-[var(--hexa-text-muted)] pl-10"
            />
          </div>
        </div>

        {/* Partecipanti */}
        <ParticipantSelector
          selectedTeamMembers={selectedTeamMembers}
          selectedContact={selectedContact}
          onTeamMembersChange={setSelectedTeamMembers}
          onContactChange={setSelectedContact}
        />

        {/* Descrizione */}
        <div className="space-y-1.5">
          <Label className="text-xs font-medium text-[var(--hexa-text-secondary)]">Descrizione</Label>
          <Textarea
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="Aggiungi dettagli sull'evento..."
            rows={3}
            className="bg-[var(--hexa-button-cancel)] border-[var(--hexa-border)] text-[var(--hexa-text-primary)] placeholder:text-[var(--hexa-text-muted)] resize-none"
          />
        </div>

        {/* Opzioni */}
        <div className="flex items-center gap-2 pt-2 border-t border-[var(--hexa-border)]">
          <span className="text-xs font-medium text-[var(--hexa-text-secondary)] mr-1">Opzioni:</span>
          {[
            { icon: Bell, label: 'Promemoria' },
            { icon: Repeat, label: 'Ricorrente' },
            { icon: Video, label: 'Videochiamata' },
          ].map(({ icon: Icon, label }) => (
            <div key={label} className="relative group">
              <button
                type="button"
                disabled
                className="p-2 rounded-lg bg-[var(--hexa-button-cancel)] text-[var(--hexa-text-muted)] opacity-50 cursor-not-allowed"
              >
                <Icon className="h-4 w-4" />
              </button>
              <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-1 px-2 py-1 rounded text-[10px] bg-[var(--hexa-surface-elevated)] text-[var(--hexa-text-secondary)] whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none">
                {label} — Coming soon
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Errore inline */}
      {saveError && (
        <div className="flex-shrink-0 px-4 py-1.5">
          <div className="text-xs text-red-400 bg-red-400/10 border border-red-400/20 rounded-lg px-2.5 py-1.5">
            {saveError}
          </div>
        </div>
      )}

      {/* Footer */}
      <div className="flex-shrink-0 border-t border-[var(--hexa-border)] px-4 py-3">
        <div className="flex gap-2.5">
          {/* Annulla Mini-Card */}
          <div className="flex-1 bg-[var(--hexa-button-cancel)] rounded-lg p-2.5 flex items-center justify-center border border-[var(--hexa-border)]">
            <Button
              variant="ghost"
              onClick={handleClose}
              disabled={isSaving}
              className="w-full text-[var(--hexa-text-secondary)] hover:text-[var(--hexa-text-primary)] disabled:opacity-50"
            >
              Annulla
            </Button>
          </div>

          {/* Salva Mini-Card */}
          <div className="flex-1 rounded-lg flex items-center justify-center">
            <Button
              onClick={handleSave}
              disabled={isSaving}
              className="w-full h-full bg-[var(--hexa-purple)] hover:bg-[var(--hexa-purple-hover)] text-white rounded-lg py-2.5 disabled:opacity-50"
            >
              {isSaving ? (
                <>
                  <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" />
                  Salvataggio...
                </>
              ) : (
                "Salva"
              )}
            </Button>
          </div>
        </div>
      </div>
    </div>
  )
}
